import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import { PRICING_PLANS } from 'src/utils/constants';
import { trackEvent, ANALYTICS_EVENTS } from 'src/utils/posthog';

import { useAuthContext } from 'src/auth/hooks';

import SocialProof from 'src/components/social-proof';

import PricingCard from './pricing-card';
import PricingSwitch from './pricing-switch';
import PricingGuarantee from './pricing-guarantee';

// ----------------------------------------------------------------------

// The plans page, matching the website's pricing page: a short header, the
// billing switch, the three plan cards side by side (equal height, so their
// buttons line up) and the guarantee underneath. Signed-in visitors see who
// they're signed in as, and returning customers are welcomed back.
export default function PricingView ({ isMainPage = false }) {
  const [yearly, setYearly] = useState(false);
  // "Every other feature" opens the list on all three cards together
  const [showAll, setShowAll] = useState(false);
  const { user, logout } = useAuthContext();

  const isReturningUser = user?.stripeCustomerId;

  useEffect(() => {
    trackEvent(ANALYTICS_EVENTS.BILLING_PRICING_VIEWED, { isMainPage });
  }, [isMainPage]);

  const renderHeader = (
    <Box sx={{ textAlign: 'center' }}>
      <Typography
        variant="h1"
        component={isMainPage ? 'h1' : 'h2'}
        sx={{
          fontWeight: 800,
          letterSpacing: '-0.02em',
          lineHeight: 1.15,
          fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3.25rem' },
          textWrap: 'balance',
        }}
      >
        {isReturningUser ? 'Welcome Back!' : 'Every Feature on Every Plan'}
      </Typography>

      <Typography
        sx={{
          mt: 1.5,
          mx: 'auto',
          maxWidth: 680,
          color: 'text.secondary',
          fontSize: { xs: '1.0625rem', md: '1.122rem' },
          lineHeight: 1.6,
          textWrap: 'pretty',
        }}
      >
        {isReturningUser
          ? 'Your data is safe and waiting for you. Pick a plan to reactivate your account.'
          : 'Pick the plan that fits how much you sell. Try it risk-free for 14 days.'}
      </Typography>

      <Box sx={{ mt: 2 }}>
        <SocialProof />
      </Box>

      {user && (
        <Typography sx={{ mt: 2, color: 'text.secondary', typography: 'body2' }}>
          Signed in as <strong>{user.email}</strong>.{' '}
          <Link component="button" variant="body2" onClick={logout} sx={{ verticalAlign: 'baseline' }}>
            Sign out
          </Link>
        </Typography>
      )}

      <Box sx={{ mt: 2.5, mb: 3.5 }}>
        <PricingSwitch checked={yearly} onChange={() => setYearly((value) => !value)} />
      </Box>
    </Box>
  );

  const renderPlans = (
    <Box
      sx={{
        display: 'grid',
        gap: 3,
        alignItems: 'stretch',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
        // Room for the "Most popular" tag over the Pro card
        pt: 1.5,
      }}
    >
      {PRICING_PLANS.map((card) => (
        <PricingCard
          key={card.subscription}
          card={card}
          yearly={yearly ? 1 : 0}
          titleComponent={isMainPage ? 'h2' : 'h3'}
          showAll={showAll}
          onToggleAll={() => setShowAll((open) => !open)}
        />
      ))}
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ pt: { xs: 4, md: 5 }, pb: 10 }}>
      {renderHeader}

      {renderPlans}

      <PricingGuarantee sx={{ mt: 5 }} />
    </Container>
  );
}

PricingView.propTypes = {
  isMainPage: PropTypes.bool,
};
